"use client";

import { PortableText } from "next-sanity";
import { motion } from "framer-motion";

import TeamMember from "@/components/global/TeamMember";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import { useCarouselApi } from "@/hooks/useCarouselApi";
import ScrollingPartnersLogos from "../global/ScrollingPartnersLogos";
import { cn } from "@/lib/utils";

const ease = [0.25, 0.1, 0.25, 1] as const;

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function TeamSection({
  data,
  language,
  showPartnersLogos,
  partnersLogos,
  className,
}: {
  data: PageHomeSchema;
  language?: string;
  showPartnersLogos?: boolean;
  partnersLogos?: Parameters<typeof ScrollingPartnersLogos>[0]["logos"];
  className?: string;
}) {
  const members = data.teamMembers ?? [];
  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } =
    useCarouselApi();

  if (!data.teamTitle && !members.length) return null;

  const progress = count > 1 ? (current - 1) / (count - 1) : 1;

  return (
    <section className={cn("w-full bg-white lg:py-0 pb-[10rem] px-[3rem] lg:px-[6rem] overflow-clip", className)}>
      <div
        className={cn(
          "relative z-[1] mx-auto flex w-full max-w-[171rem] lg:border-t-[.125rem] border-black/10 flex-col items-center gap-[8rem] lg:gap-[10.625rem] lg:py-[15.625rem] overflow-visible",
          showPartnersLogos && "gap-[2rem] lg:gap-[4.5rem]",
        )}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.5rem] text-center lg:w-[83rem] w-[44rem]"
        >
          {data.teamChip && (
            <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
              {data.teamChip}
            </p>
          )}

          {data.teamTitle && (
            <div className="richtext flex flex-col gap-[1.5rem]">
              <PortableText
                value={data.teamTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {showPartnersLogos && (
          <ScrollingPartnersLogos
            logos={partnersLogos ?? data.partnersLogos ?? []}
            theme="dark-tinted"
            className="py-0"
          />
        )}

        {!!members.length && (
          <div className="flex flex-col gap-[4rem] w-full">
            <Carousel
              setApi={setApi}
              opts={{ align: "start", loop: false }}
              className="w-full !overflow-visible"
            >
              <CarouselContent className="!overflow-visible gap-[3rem]">
                {members.map((member) => (
                  <CarouselItem
                    key={member._id ?? member.name}
                    className="pl-0 basis-full md:basis-1/2 xl:basis-1/3"
                  >
                    <TeamMember member={member} language={language} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {count > 1 && (
              <div className="flex items-center gap-[2rem] w-[min(50rem,100%)] mx-auto">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={isFirst}
                  aria-label="Previous"
                  className="flex items-center justify-center w-[4.25rem] h-[4.25rem] rounded-full shrink-0 bg-black/10 transition-opacity disabled:opacity-30 hover:opacity-70"
                >
                  <ArrowIcon className="w-[2.125rem] h-[2.125rem] text-black rotate-180" />
                </button>

                <div className="flex-1 h-[0.5rem] rounded-[3rem] bg-black/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-black rounded-full"
                    animate={{ width: `${progress * 100}%` }}
                    transition={{ duration: 0.3, ease }}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={isLast}
                  aria-label="Next"
                  className="flex items-center justify-center w-[4.25rem] h-[4.25rem] rounded-full shrink-0 bg-black hover:opacity-80 transition-opacity disabled:opacity-30"
                >
                  <ArrowIcon className="w-[2.125rem] h-[2.125rem] text-white" />
                </button>
              </div>
            )}
          </div>
        )}

        {data.teamButton && (
          <div className="w-full lg:w-fit flex justify-center">
            <CTAButton data={data.teamButton} />
          </div>
        )}
      </div>
    </section>
  );
}
